import { useEffect, useState } from "react";
import { GrButton, GrStack, GrTextField, GrTypography, palette } from "@jnpmedi/grebob";
import { useParams } from "react-router-dom";
import isEmail from "validator/lib/isEmail";
import { DocumentSignerView } from "@newPages/sign/DocumentSignerView";
import { StyledTokenView, StyledTokenViewVerifyInnerStack, StyledTokenViewVerifyStack } from "@newComponents/common/Common.styled";
import { ViewportCenterCircle } from "@newComponents/common/ViewportCenterCircle";
import { InvalidAccessView } from "@newComponents/InvalidAccessView";
import { InformationGuide } from "@newComponents/layout/InformationGuide";
import { IdentificationButtonByDanal } from "@newComponents/myDocument/IdentificationButtonByDanal";
import { isCanceledDocumentSign, langHelper, MvnMessage } from "@newLibs/utils";
import { useBoolean } from "@newHooks/common/useBoolean";
import { useInterval } from "@newHooks/common/useInterval";
import { notify } from "@newHooks/context/snackbar/notify";
import { useEmailVerificationCodeBySignerTokenMutation } from "@newHooks/mutation/signers/emailVerificationCodeBySignerToken";
import { useDocumentSignBySignerTokenQuery } from "@newHooks/query/documentSigner/documentSignBySignerToken.query";
import { useRoute } from "@newHooks/route/useRoute";
import { DOCS_DOCUMENT_CODE } from "@maven-msg/code/docs";
import { PERSONAL_IDENTIFICATION_CODE } from "@maven-msg/code/santa";
import { AUTH_USER_VERIFICATION_CODE_LANG } from "@maven-msg/lang/auth";
import { DOCS_DOCUMENT_LANG, DOCS_DOCUMENT_SIGN_LANG } from "@maven-msg/lang/docs";
import { DocumentSignResource, DocumentSignStatus } from "@maven-rest/docs/DocumentSign.dto";
import { FolderFileType } from "@maven-rest/docs/FolderFile.dto";

const FETCHABLE_DOCUMENT_SIGN_STATUS: Array<DocumentSignStatus> = [DocumentSignStatus.Signed, DocumentSignStatus.Finalizing];

const DOCUMENT_SIGN_FINALIZED_QS_KEY = "document_sign_finalized";
const DOCUMENT_SIGN_ALL_COMPLETED_QS_KEY = "document_sign_all_completed";
const DOCUMENT_SIGN_DENIED_QS_KEY = "document_sign_denied";
export const DOCUMENT_SIGN_PDF_PAGE_CACHE_PREFIX = "DocsPdfViewerPage";
export const DOCUMENT_SIGN_PDF_PAGE_CACHE_DELIMITER = ":";

export function SignerTokenView() {
  const { token } = useParams<{ token: string }>();
  const { query, removeQuery } = useRoute();
  const lang = langHelper.getLang();

  const accessToken = String(query.access_token || "") || undefined;
  const personalIdentificationEncrypted = String(query.ci || "") || undefined;

  const [email, setEmail] = useState<string>("");
  const [emailVerificationCode, setEmailVerificationCode] = useState<string>("");
  const [emailVerificationUuid, setEmailVerificationUuid] = useState<string>("");
  const [documentSignResource, setDocumentSignResource] = useState<DocumentSignResource>();
  const isIntervalPause = useBoolean(true);

  useEffect(() => {
    const documentSignFinalized = query.document_sign_finalized;
    const documentSignDenied = query.document_sign_denied;
    const documentSignAllCompleted = query.document_sign_all_completed;

    if (documentSignFinalized && Boolean(documentSignFinalized) === true) {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_ESIGN_SUCC), "SUCC");
      removeQuery(DOCUMENT_SIGN_FINALIZED_QS_KEY);
    }

    if (documentSignDenied && Boolean(documentSignDenied) === true) {
      notify(MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_DECLINE_SUCC), "SUCC");
      removeQuery(DOCUMENT_SIGN_DENIED_QS_KEY);
    }

    if (documentSignAllCompleted && Boolean(documentSignAllCompleted) === true) {
      notify(MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_FILES_CREATED), "SUCC");
      removeQuery(DOCUMENT_SIGN_ALL_COMPLETED_QS_KEY);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDocumentSignStatus = (status: DocumentSignStatus) => {
    if ([DocumentSignStatus.Finalized, DocumentSignStatus.FinalizingFailed].includes(status)) {
      isIntervalPause.onTrue();
      return;
    }

    if (FETCHABLE_DOCUMENT_SIGN_STATUS.includes(status)) {
      isIntervalPause.onFalse();
    }
  };

  // NOTE: 초기 요청용  query
  const {
    error: documentSignBySignerTokenError,
    isInitialLoading: isDocumentSignBySignerTokenInitialLoading,
    refetch: refetchDocumentSignBySignerToken,
  } = useDocumentSignBySignerTokenQuery(
    {
      token,
      accessToken,
      personalIdentificationEncrypted,
    },
    {
      onSuccess: (data) => {
        const documentSignResult = data.documentSign;
        setDocumentSignResource(documentSignResult);
        handleDocumentSignStatus(documentSignResult.status);
      },
      enabled: !documentSignResource,
      allowWithoutSession: true,
    }
  );

  // NOTE: interval 수행용 refetch query
  const { refetch: refetchDocumentSignBySignerTokenRequestInterval } = useDocumentSignBySignerTokenQuery(
    {
      token,
      accessToken,
      personalIdentificationEncrypted,
    },
    { enabled: false, allowWithoutSession: true }
  );

  // NOTE: email 인증 요청용 refetch query
  const {
    error: emailVerifyQueryError,
    refetch: refetchDocumentSignBySignerTokenRequestByEmailVerificationCode,
    isInitialLoading: isDocumentSignBySignerTokenRequestByEmailVerificationCodeInitialLoading,
  } = useDocumentSignBySignerTokenQuery(
    {
      token,
      accessToken,
      personalIdentificationEncrypted,
      emailVerificationCode,
      emailVerificationUuid,
    },
    {
      onSuccess: (data) => {
        handleDocumentSignStatus(data.documentSign.status);
        setDocumentSignResource(data.documentSign);
      },

      enabled: false,
      allowWithoutSession: true,
      queryKey: ["emailVerify-query"],
    }
  );

  const { mutate: emailVerificationCodeBySignerToken, isLoading } = useEmailVerificationCodeBySignerTokenMutation({
    onSuccess: (resp) => {
      setEmailVerificationUuid(resp.userVerificationCode.uuid);
      notify({ message: MvnMessage(AUTH_USER_VERIFICATION_CODE_LANG.USER_VERIFICATION_CODE_EMAIL_SEND_SUCC), severity: "success" });
    },
    onError: (error) => {
      notify(
        MvnMessage(AUTH_USER_VERIFICATION_CODE_LANG.USER_VERIFICATION_CODE_EMAIL_SEND_FAIL, { message: error.responseMessage }),
        "WARN"
      );
    },
  });

  const removeAllDocsPdfPageCaches = () => {
    const cachedSvgKeyPrefix = `${DOCUMENT_SIGN_PDF_PAGE_CACHE_PREFIX}${DOCUMENT_SIGN_PDF_PAGE_CACHE_DELIMITER}`;

    Object.keys(sessionStorage)
      .filter((sessionKey) => sessionKey.startsWith(cachedSvgKeyPrefix))
      .forEach((matchedCacheKey) => sessionStorage.removeItem(matchedCacheKey));
  };

  useInterval(
    () => {
      refetchDocumentSignBySignerTokenRequestInterval().then((res) => {
        const documentSign = res.data?.documentSign;

        documentSign && handleDocumentSignStatus(documentSign.status);
        if (documentSign && [DocumentSignStatus.FinalizingFailed, DocumentSignStatus.Finalized].includes(documentSign.status)) {
          isIntervalPause.onTrue();
          removeAllDocsPdfPageCaches();

          window.document.location.href = `${document.location.pathname}?access_token=${encodeURIComponent(
            documentSign.accessToken
          )}&${DOCUMENT_SIGN_ALL_COMPLETED_QS_KEY}=true`;
        }
      });
    },
    1000,
    isIntervalPause.value
  );

  const handleDanalAuthCallbackFn = (resp: DocumentSignResource | null, encrypted: string | null) => {
    if (resp) {
      setDocumentSignResource(resp);
      return;
    }

    if (encrypted) {
      document.location.href = `${document.location.pathname}?ci=${encodeURIComponent(encrypted)}`;
    }

    setTimeout(() => {
      refetchDocumentSignBySignerToken();
    }, 5000);
  };

  const renderDocumentSignerTokenErrorView = () => {
    if (!documentSignBySignerTokenError) {
      return null;
    }

    const identityRegistration =
      documentSignBySignerTokenError?.responseCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_REQUIRED_IDENTITY_VERIFICATION.code;
    const emailRegistration =
      documentSignBySignerTokenError?.responseCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_REQUIRED_EMAIL_REGISTRATION.code;
    const emailVerification =
      documentSignBySignerTokenError?.responseCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_REQUIRED_EMAIL_VERIFICATION.code;

    if (identityRegistration) {
      return (
        <StyledTokenViewVerifyStack>
          <StyledTokenViewVerifyInnerStack>
            <GrStack flexDirection="column" gap={16}>
              <GrStack flexDirection="column" gap={24}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <GrTypography type="Content.Title02" color="grey080">
                    {MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_IDENTITY_VERIFY_HEADER)}
                  </GrTypography>
                </div>
                <GrTypography type="Content.Body03" color="grey080">
                  {MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_IDENTITY_VERIFY_DESCRIPTION)}
                </GrTypography>
              </GrStack>
              <IdentificationButtonByDanal
                token={token}
                buttonId="maven-docs-sign--identity"
                callbackUrl={process.env.REACT_APP_DOCS_FE_URL || ""}
                callbackFunction={handleDanalAuthCallbackFn}
              />
            </GrStack>
          </StyledTokenViewVerifyInnerStack>
        </StyledTokenViewVerifyStack>
      );
    }

    if (emailRegistration || emailVerification) {
      return (
        <StyledTokenViewVerifyStack>
          <StyledTokenViewVerifyInnerStack>
            <GrStack flexDirection="column" gap={16}>
              <GrStack flexDirection="column" gap={24}>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <GrTypography type="Content.Title02" color="grey080">
                    {emailRegistration && MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_EMAIL_REGISTER_HEADER)}
                    {emailVerification && MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_EMAIL_VERIFY_HEADER)}
                  </GrTypography>
                </div>
                <GrTypography type="Content.Body03" color="grey080">
                  {emailRegistration && MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_EMAIL_REGISTER_DESCRIPTION)}
                  {emailVerification && MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_EMAIL_VERIFY_DESCRIPTION)}
                </GrTypography>
              </GrStack>

              <GrStack flexDirection="column" gap={8}>
                <GrTextField
                  fullWidth
                  autoFocus
                  placeholder={MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_EMAIL_PLACEHOLDER)}
                  value={email}
                  onChange={(event) => setEmail(event.currentTarget.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !!email && isEmail(email)) {
                      emailVerificationCodeBySignerToken({ token, email, lang });
                    }
                  }}
                  disabled={!!emailVerificationUuid}
                  error={!!email && !isEmail(email)}
                  helperText={
                    !!email && !isEmail(email)
                      ? {
                          text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_EMAIL_VALIDATION_INVALID),
                          hasIcon: true,
                          style: { color: palette["red050"] },
                        }
                      : undefined
                  }
                />
                {emailVerificationUuid && (
                  <GrTextField
                    fullWidth
                    type="number"
                    placeholder={MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_CODE_PLACEHOLDER)}
                    value={emailVerificationCode}
                    onChange={(event) => setEmailVerificationCode(event.currentTarget.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !!emailVerificationCode) {
                        refetchDocumentSignBySignerTokenRequestByEmailVerificationCode();
                      }
                    }}
                    error={!!emailVerifyQueryError}
                    helperText={
                      !!emailVerifyQueryError
                        ? {
                            text: MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_EMAIL_VERIFICATION_CODE_INVALID),
                            hasIcon: true,
                            style: { color: palette["red050"] },
                          }
                        : undefined
                    }
                  />
                )}
                {/* 인증번호 요청하기 */}
                {!emailVerificationUuid && (
                  <GrButton
                    fullWidth
                    disabled={!email || !isEmail(email)}
                    isLoading={isLoading}
                    onClick={() => {
                      emailVerificationCodeBySignerToken({ token, email, lang });
                    }}
                  >
                    <GrTypography type="Content.Subtitle03">
                      {MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_EMAIL_REQUEST_VERIFY_CODE)}
                    </GrTypography>
                  </GrButton>
                )}
                {/* 인증번호 기입하고 인증하기 버튼 */}
                {emailVerificationUuid && (
                  <GrButton
                    fullWidth
                    disabled={!emailVerificationCode}
                    isLoading={isDocumentSignBySignerTokenRequestByEmailVerificationCodeInitialLoading}
                    onClick={() => refetchDocumentSignBySignerTokenRequestByEmailVerificationCode()}
                  >
                    <GrTypography type="Content.Subtitle03">
                      {MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_PROCEED_VERIFY)}
                    </GrTypography>
                  </GrButton>
                )}
              </GrStack>
            </GrStack>
          </StyledTokenViewVerifyInnerStack>
        </StyledTokenViewVerifyStack>
      );
    }

    if (
      [
        PERSONAL_IDENTIFICATION_CODE.PERSONAL_IDENTIFICATION_DECRYPTION_EXPIRED.code,
        PERSONAL_IDENTIFICATION_CODE.PERSONAL_IDENTIFICATION_DECRYPTION_FAILED.code,
        PERSONAL_IDENTIFICATION_CODE.PERSONAL_IDENTIFICATION_DECRYPTION_INVALID.code,
      ].includes(documentSignBySignerTokenError?.responseCode)
    ) {
      setTimeout(() => {
        document.location.href = `${document.location.pathname}`;
      }, 2500);
    }

    return (
      <InformationGuide
        style={{ height: "100vh", backgroundColor: "inherit" }}
        title={MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_FETCH_FAIL, {
          message: documentSignBySignerTokenError?.responseMessage || "An error has occurred.",
        })}
        description=""
        emptyType="noFile"
      />
    );
  };

  if (isDocumentSignBySignerTokenInitialLoading) {
    return <ViewportCenterCircle />;
  }

  return (
    <StyledTokenView>
      {documentSignResource && isCanceledDocumentSign(documentSignResource.status) && (
        <InvalidAccessView accessType={FolderFileType.Sign} />
      )}
      {documentSignResource && !isCanceledDocumentSign(documentSignResource.status) && (
        <DocumentSignerView
          documentSign={documentSignResource}
          onDocumentRefresh={refetchDocumentSignBySignerToken}
          isDownloadFilesCreating={!isIntervalPause.value}
        />
      )}

      {!documentSignResource && <> {renderDocumentSignerTokenErrorView()} </>}
    </StyledTokenView>
  );
}
